import { parse } from 'tldts';

import { extractViewerDomain } from './partnerViewerScope';
import { isLvoColumnChecked } from './roleManagementLvoColumn';

/**
 * Remove only the recognized public suffix from a full host.
 * `parse(host).publicSuffix` then delete `.{suffix}` — `lab.vertivco.com` → `lab.vertivco`.
 * `getDomain` / `getDomainWithoutSuffix` return the registrable domain and are the wrong value.
 * Keep this strip aligned with `server/utils/partnerNameFromEmail.ts`.
 */
function partnerNameFromHost(host: string): string {
  const parsed = parse(host);
  // tldts reports the last label for an unknown host (`foo.local` → `local`)
  // with isIcann false. Only an ICANN suffix is a recognized public suffix.
  const suffix = parsed.isIcann === true ? parsed.publicSuffix : null;
  if (!suffix) {
    return '';
  }
  const marker = `.${suffix.toLowerCase()}`;
  const normalized = host.toLowerCase();
  if (!normalized.endsWith(marker) || normalized.length <= marker.length) {
    return '';
  }
  return normalized.slice(0, normalized.length - marker.length);
}

/** Partner Name prefill from an email. `''` when the host cannot be parsed or has no public suffix. */
export function partnerNameFromEmail(email: string | null | undefined): string {
  const host = extractViewerDomain(email);
  if (!host) {
    return '';
  }
  return partnerNameFromHost(host);
}

/**
 * Partner Name treats a host as internal when it contains `vertiv`.
 * The mailbox name is not checked. This is not `RoleAssignmentService`.
 */
export function isVertivPartnerNameHost(
  host: string | null | undefined,
): boolean {
  return typeof host === 'string' && host.toLowerCase().includes('vertiv');
}

/**
 * Vertiv hosts and anyone with the Users-table LVO checkbox choose Partner
 * Name. An external host without that role is locked to the stripped domain.
 * The checkbox is either paired LVO role (`External Requestor LVO` or
 * `Partner Contributor LVO`).
 */
export function partnerNameActorChooses(input: {
  host: string | null | undefined;
  roles?: readonly string[] | null;
}): boolean {
  if (isVertivPartnerNameHost(input.host)) {
    return true;
  }
  if (!input.roles || input.roles.length === 0) {
    return false;
  }
  return isLvoColumnChecked(new Set(input.roles));
}

/**
 * Email that drives Partner Name for the person actually using the request.
 * While impersonating, that is the impersonated person. The signed-in
 * viewer's email can still be the impersonator and must not be used.
 */
export function partnerNameActorEmail(input: {
  isImpersonating: boolean;
  impersonatedEmail?: string | null;
  viewerEmail?: string | null;
}): string | null | undefined {
  if (input.isImpersonating) {
    return input.impersonatedEmail;
  }
  return input.viewerEmail;
}

export type CreatePartnerNamePrefillInput = {
  hydrateComplete: boolean;
  userEditedPartnerName: boolean;
  draftStoredPartnerName: boolean;
  partnerName: unknown;
  requestorEmail: string | null | undefined;
  actorRoles?: readonly string[] | null;
};

/**
 * Value to write onto a Create Request, or null when this session must leave
 * Partner Name alone. A host that contains `vertiv`, or an actor with an LVO
 * role, stays blank (a stored draft value wins). Every other host is forced
 * to the suffix-stripped name after hydration, including a typed value or a
 * draft that stored the key.
 */
export function resolveCreatePartnerNamePrefill(
  input: CreatePartnerNamePrefillInput,
): string | null {
  if (!input.hydrateComplete) {
    return null;
  }
  const host = extractViewerDomain(input.requestorEmail);
  if (partnerNameActorChooses({ host, roles: input.actorRoles })) {
    return null;
  }
  const prefill = partnerNameFromEmail(input.requestorEmail);
  return prefill === '' ? null : prefill;
}
